import React, { useState, useEffect } from 'react'
import { TrendingUp, Users, Star, Activity, Eye, Download, Mail, Key } from 'lucide-react'
import { useTenant } from '../contexts/TenantContext'

interface UserEngagement {
  userEmail: string
  userCompany?: string
  score: number
  lastActivity: string
  activities: {
    pageViews: number
    documentDownloads: number
    documentViews: number
    emailCaptured: boolean
    accessRequested: boolean
    lastVisit: string
  }
  scoreBreakdown: {
    recency: number
    frequency: number
    depth: number
    intent: number
  }
}

interface EngagementSummary {
  totalUsers: number
  highValueProspects: number
  scores: UserEngagement[]
  summary: {
    averageScore: number
    topScore: number
    activeLastWeek: number
  }
}

interface EngagementDashboardProps {
  embedded?: boolean
}

const EngagementDashboard: React.FC<EngagementDashboardProps> = ({ embedded = false }) => {
  const { currentTenant } = useTenant()
  const [data, setData] = useState<EngagementSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [accessDenied, setAccessDenied] = useState(false)

  useEffect(() => {
    if (currentTenant) {
      fetchEngagementData()
    }
  }, [currentTenant])

  const fetchEngagementData = async () => {
    if (!currentTenant) {
      console.error('[EngagementDashboard] Cannot fetch engagement data without currentTenant')
      setError('No tenant selected')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const response = await fetch(`/api/engagement-score?tenantId=${currentTenant.id}`)

      if (!response.ok) {
        // Gracefully handle authentication/authorization errors by hiding component
        if (response.status === 401 || response.status === 403) {
          setAccessDenied(true)
          setLoading(false)
          return
        }

        const errorBody = await response.text()
        console.error(`[EngagementDashboard] API returned ${response.status}: ${errorBody}`)
        throw new Error(`Failed to fetch engagement data (HTTP ${response.status}): ${errorBody}`)
      }

      const engagementData = await response.json()
      setData(engagementData)
    } catch (err) {
      // Network errors - fail silently
      setAccessDenied(true)
    } finally {
      setLoading(false)
    }
  }

  const getScoreBadge = (score: number) => {
    if (score >= 80) return { color: 'bg-green-100 text-green-800', label: 'Hot Lead' }
    if (score >= 60) return { color: 'bg-blue-100 text-blue-800', label: 'Engaged' }
    if (score >= 40) return { color: 'bg-yellow-100 text-yellow-800', label: 'Interested' }
    return { color: 'bg-gray-100 text-gray-800', label: 'Cold' }
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-blue-600'
    if (score >= 40) return 'text-yellow-600'
    return 'text-gray-600'
  }

  if (loading) {
    return (
      <div className={embedded ? "p-8" : "min-h-screen bg-gray-50 flex items-center justify-center"}>
        <div className="text-center">
          <TrendingUp className="h-12 w-12 text-primary-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Calculating engagement scores...</p>
        </div>
      </div>
    )
  }

  // Hide component gracefully when access is denied
  if (accessDenied) {
    return null
  }

  if (error || !data) {
    return (
      <div className={embedded ? "p-8" : "min-h-screen bg-gray-50 flex items-center justify-center"}>
        <div className="text-center">
          <Activity className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">{error || 'Failed to load engagement data'}</p>
        </div>
      </div>
    )
  }

  const content = (
    <div className={embedded ? "" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"}>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Visitors</p>
                <p className="text-2xl font-bold text-gray-900">{data.totalUsers}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Hot Leads</p>
                <p className="text-2xl font-bold text-gray-900">{data.highValueProspects}</p>
              </div>
              <Star className="h-8 w-8 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg Score</p>
                <p className="text-2xl font-bold text-gray-900">{Math.round(data.summary.averageScore)}</p>
              </div>
              <Activity className="h-8 w-8 text-purple-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active This Week</p>
                <p className="text-2xl font-bold text-gray-900">{data.summary.activeLastWeek}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-orange-500" />
            </div>
          </div>
        </div>

        {/* Engagement Score Explanation */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">How Engagement Scores Work</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-blue-800">
            <div>
              <p className="font-semibold mb-1">Recency (0-25)</p>
              <p className="text-blue-700">How recent was their last visit?</p>
            </div>
            <div>
              <p className="font-semibold mb-1">Frequency (0-25)</p>
              <p className="text-blue-700">How often do they visit?</p>
            </div>
            <div>
              <p className="font-semibold mb-1">Depth (0-30)</p>
              <p className="text-blue-700">How much do they explore?</p>
            </div>
            <div>
              <p className="font-semibold mb-1">Intent (0-20)</p>
              <p className="text-blue-700">Did they take high-intent actions?</p>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">All Prospects</h2>
          </div>

          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prospect
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Activity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Visit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score Breakdown
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.scores.map((user, index) => {
                const badge = getScoreBadge(user.score)
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.userEmail}</div>
                        {user.userCompany && (
                          <div className="text-sm text-gray-500">{user.userCompany}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`text-2xl font-bold mr-3 ${getScoreColor(user.score)}`}>
                          {user.score}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                          {badge.label}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-3 text-sm text-gray-600">
                        <span className="flex items-center" title="Page Views">
                          <Eye className="h-4 w-4 mr-1" />
                          {user.activities.pageViews}
                        </span>
                        <span className="flex items-center" title="Document Downloads">
                          <Download className="h-4 w-4 mr-1" />
                          {user.activities.documentDownloads}
                        </span>
                        {user.activities.emailCaptured && (
                          <span className="flex items-center text-green-600" title="Email Captured">
                            <Mail className="h-4 w-4" />
                          </span>
                        )}
                        {user.activities.accessRequested && (
                          <span className="flex items-center text-blue-600" title="Access Requested">
                            <Key className="h-4 w-4" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.activities.lastVisit).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs space-y-1">
                        <div className="flex items-center">
                          <span className="w-16 text-gray-600">Recency:</span>
                          <div className="flex-1 bg-gray-200 rounded-full h-2 ml-2">
                            <div
                              className="bg-blue-500 h-2 rounded-full"
                              style={{ width: `${(user.scoreBreakdown.recency / 25) * 100}%` }}
                            />
                          </div>
                          <span className="ml-2 w-8 text-right">{user.scoreBreakdown.recency}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="w-16 text-gray-600">Frequency:</span>
                          <div className="flex-1 bg-gray-200 rounded-full h-2 ml-2">
                            <div
                              className="bg-green-500 h-2 rounded-full"
                              style={{ width: `${(user.scoreBreakdown.frequency / 25) * 100}%` }}
                            />
                          </div>
                          <span className="ml-2 w-8 text-right">{user.scoreBreakdown.frequency}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="w-16 text-gray-600">Depth:</span>
                          <div className="flex-1 bg-gray-200 rounded-full h-2 ml-2">
                            <div
                              className="bg-purple-500 h-2 rounded-full"
                              style={{ width: `${(user.scoreBreakdown.depth / 30) * 100}%` }}
                            />
                          </div>
                          <span className="ml-2 w-8 text-right">{user.scoreBreakdown.depth}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="w-16 text-gray-600">Intent:</span>
                          <div className="flex-1 bg-gray-200 rounded-full h-2 ml-2">
                            <div
                              className="bg-orange-500 h-2 rounded-full"
                              style={{ width: `${(user.scoreBreakdown.intent / 20) * 100}%` }}
                            />
                          </div>
                          <span className="ml-2 w-8 text-right">{user.scoreBreakdown.intent}</span>
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
  )

  if (embedded) {
    return content
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-primary-600" />
              <h1 className="ml-3 text-2xl font-bold text-gray-900">Engagement Scoring</h1>
            </div>
            <a href="/admin" className="text-primary-600 hover:text-primary-700">← Back to Admin</a>
          </div>
        </div>
      </header>

      {content}
    </div>
  )
}

export default EngagementDashboard
